// src/features/bases/atividades/AtividadesPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import api from "../../../api/axios";
import "./AtividadesPage.css";
import "../MaintenanceTheme.css";

import CriarModal from "./popups/CriarModal.jsx";
import EditarModal from "./popups/EditarModal.jsx";
import ExcluirModal from "./popups/ExcluirModal.jsx";
import AtivarModal from "./popups/AtivarModal.jsx";
import InativarModal from "./popups/InativarModal.jsx";

const UP = (v) => (typeof v === "string" ? v.toUpperCase() : v ?? "");

const LABELS = {
  atividade: "ATIVIDADE",
  unidade: "UNIDADE",
  controle_indicador: "CONTROLE DO INDICADOR",
  grupo: "GRUPO",
  item_pdm: "ITEM PDM",
};

const ORDER = ["atividade", "unidade", "controle_indicador", "grupo", "item_pdm"];

const COLW = {
  atividade: "25%",
  controle_indicador: "28%",
  acoes: "16%",
  default: "10.3%",
};

function IconEdit(props) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function IconTrash(props) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function ToggleSwitch({ on = false, onToggle, title }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      className={`toggle ${on ? "on" : "off"}`}
      onClick={onToggle}
      title={title || (on ? "Inativar" : "Ativar")}
    >
      <span className="knob" />
    </button>
  );
}

function computeStats(list) {
  const byGroup = {};
  let ativos = 0;

  for (const r of list) {
    if (Number(r?.ativo)) ativos++;
    const g = UP(r?.grupo || "-");
    byGroup[g] = (byGroup[g] || 0) + 1;
  }

  const inativos = list.length - ativos;
  const porGrupo = Object.entries(byGroup).sort((a, b) =>
    a[0].localeCompare(b[0], "pt-BR", { sensitivity: "base" })
  );

  return { total: list.length, ativos, inativos, porGrupo, byGroup };
}

function renderCell(v) {
  if (v == null || v === "") return "-";
  return typeof v === "string" ? v.toUpperCase() : String(v);
}

export default function AtividadesPage({ onBack }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [openCriar, setOpenCriar] = useState(false);
  const [openEditar, setOpenEditar] = useState(false);
  const [openExcluir, setOpenExcluir] = useState(false);
  const [openAtivar, setOpenAtivar] = useState(false);
  const [openInativar, setOpenInativar] = useState(false);
  const [selecionada, setSelecionada] = useState(null);

  const [grupoFiltro, setGrupoFiltro] = useState(null);

  useEffect(() => {
    document.body.classList.add("ativ-dark");
    return () => document.body.classList.remove("ativ-dark");
  }, []);

  async function carregar({ silent = false } = {}) {
    if (!silent) setLoading(true);
    setErr("");

    try {
      const r = await api.get("/atividades/listar", { params: {} });
      setRows(r.data?.data || []);
    } catch (e) {
      setErr(e?.response?.data?.error || e.message || "Erro ao listar");
      setRows([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }

  useEffect(() => {
    carregar();
  }, []);

  const stats = useMemo(() => computeStats(rows), [rows]);

  const filtradas = useMemo(() => {
    const arr = grupoFiltro ? rows.filter((r) => UP(r?.grupo) === UP(grupoFiltro)) : rows;
    const a = [...arr];
    a.sort((x, y) =>
      String(x?.atividade || "").localeCompare(String(y?.atividade || ""), "pt-BR", { sensitivity: "base" })
    );
    return a;
  }, [rows, grupoFiltro]);

  async function criarAtividade(payload) {
    try {
      const r = await api.post("/atividades/cadastrar", payload);
      const id = r.data?.id;
      setRows((prev) => [{ id, ...payload }, ...prev]);
      setOpenCriar(false);
      carregar({ silent: true });
    } catch (e) {
      alert(e?.response?.data?.error || "Erro ao cadastrar");
    }
  }

  async function editarAtividade(payload) {
    if (!selecionada?.id) return;
    const id = selecionada.id;

    try {
      await api.put(`/atividades/editar/${id}`, payload);
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...payload } : r)));
      setOpenEditar(false);
      setSelecionada(null);
      carregar({ silent: true });
    } catch (e) {
      alert(e?.response?.data?.error || "Erro ao editar");
    }
  }

  async function excluirAtividade() {
    if (!selecionada?.id) return;
    const id = selecionada.id;

    try {
      await api.delete(`/atividades/excluir/${id}`);
      setRows((prev) => prev.filter((r) => r.id !== id));
      setOpenExcluir(false);
      setSelecionada(null);
    } catch (e) {
      alert(e?.response?.data?.error || "Erro ao excluir");
    }
  }

  async function setAtivo(ativo) {
    if (!selecionada?.id) return;
    const id = selecionada.id;

    try {
      await api.patch(`/atividades/alterar-ativo/${id}`, { ativo });
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ativo } : r)));
      setOpenAtivar(false);
      setOpenInativar(false);
      setSelecionada(null);
    } catch (e) {
      alert(e?.response?.data?.error || "Erro ao alterar status");
    }
  }

  function goHome() {
    if (onBack) onBack();
    else if (window.history.length > 1) window.history.back();
    else window.location.href = "/";
  }

  function renderColGroup() {
    return (
      <colgroup>
        <col style={{ width: COLW.atividade }} />
        <col style={{ width: COLW.default }} />
        <col style={{ width: COLW.controle_indicador }} />
        <col style={{ width: COLW.default }} />
        <col style={{ width: COLW.default }} />
        <col style={{ width: COLW.acoes }} />
      </colgroup>
    );
  }

  return (
    <div className="app-shell maint-theme maint-atividades">
      <div className="topbar">
        <button className="btn-hero left" type="button" onClick={goHome}>
          Voltar para o inicio
        </button>
        <h1 className="title">Catálago de Atividades</h1>
        <button className="btn-hero right" type="button" onClick={() => setOpenCriar(true)}>
          Cadastrar
        </button>
      </div>

      <div className="cards">
        <div className="card">
          <div className="card__label">ATIVIDADES</div>
          <div className="card__value">{stats.total}</div>
        </div>
        <div className="card card--ok">
          <div className="card__label">ATIVAS</div>
          <div className="card__value">{stats.ativos}</div>
        </div>
        <div className="card card--alert">
          <div className="card__label">INATIVAS</div>
          <div className="card__value">{stats.inativos}</div>
        </div>

        <div className="card card--groups">
          <div className="card__label">POR GRUPO</div>
          <div className="group-list">
            {stats.porGrupo.map(([g, q]) => (
              <button
                key={g}
                className={`chip ${grupoFiltro && UP(grupoFiltro) === UP(g) ? "active" : ""}`}
                type="button"
                onClick={() => setGrupoFiltro((prev) => (prev && UP(prev) === UP(g) ? null : g))}
                title={`Filtrar por ${g}`}
              >
                {g}
                <span className="chip__num">{q}</span>
              </button>
            ))}
            {grupoFiltro && (
              <button className="chip chip--clear" type="button" onClick={() => setGrupoFiltro(null)} title="Limpar filtro">
                Limpar filtro x
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="table-wrap">
        <table className="grid-table grid-table--head">
          {renderColGroup()}
          <thead>
            <tr>
              {ORDER.map((key) => (<th key={key}>{LABELS[key]}</th>))}
              <th>CONTROLES</th>
            </tr>
          </thead>
        </table>

        <div className="table-body-scroll">
          <table className="grid-table grid-table--body">
            {renderColGroup()}
            <tbody>
              {loading ? (
                <tr><td className="td" colSpan={ORDER.length + 1}>Carregando...</td></tr>
              ) : err ? (
                <tr><td className="td" colSpan={ORDER.length + 1}>{err}</td></tr>
              ) : filtradas.length === 0 ? (
                <tr><td className="td" colSpan={ORDER.length + 1}>Nenhuma atividade encontrada.</td></tr>
              ) : (
                filtradas.map((row) => (
                  <tr key={row.id ?? JSON.stringify(row)}>
                    {ORDER.map((key) => <td key={key} className="td">{renderCell(row[key])}</td>)}
                    <td className="td td-actions">
                      <div className="actions actions--cluster">
                        <ToggleSwitch
                          on={!!Number(row.ativo)}
                          onToggle={() => {
                            setSelecionada(row);
                            if (Number(row.ativo)) setOpenInativar(true);
                            else setOpenAtivar(true);
                          }}
                          title={Number(row.ativo) ? "Inativar" : "Ativar"}
                        />
                        <span className="actions-divider" aria-hidden="true" />
                        <button
                          type="button"
                          className="icon-btn"
                          title="Editar"
                          onClick={() => {
                            setSelecionada(row);
                            setOpenEditar(true);
                          }}
                        >
                          <IconEdit />
                        </button>
                        <span className="actions-divider" aria-hidden="true" />
                        <button
                          type="button"
                          className="icon-btn"
                          title="Excluir"
                          onClick={() => {
                            setSelecionada(row);
                            setOpenExcluir(true);
                          }}
                        >
                          <IconTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CriarModal open={openCriar} onClose={() => setOpenCriar(false)} onSalvar={criarAtividade} />
      <EditarModal
        open={openEditar}
        registro={selecionada}
        onClose={() => {
          setOpenEditar(false);
          setSelecionada(null);
        }}
        onSalvar={editarAtividade}
      />
      <ExcluirModal
        open={openExcluir}
        titulo={selecionada?.atividade}
        onClose={() => {
          setOpenExcluir(false);
          setSelecionada(null);
        }}
        onConfirmar={excluirAtividade}
      />
      <AtivarModal
        open={openAtivar}
        titulo={selecionada?.atividade}
        onClose={() => {
          setOpenAtivar(false);
          setSelecionada(null);
        }}
        onConfirmar={() => setAtivo(1)}
      />
      <InativarModal
        open={openInativar}
        titulo={selecionada?.atividade}
        onClose={() => {
          setOpenInativar(false);
          setSelecionada(null);
        }}
        onConfirmar={() => setAtivo(0)}
      />
    </div>
  );
}
